import { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { FiSearch } from "react-icons/fi";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import { toast } from "react-toastify";
import { UpdateArchivedStatusOfChannelAsync } from "../../../services/channelService/ChannelService";
import { UpdateArchivedStatusOfChannelReq } from "../../../interfaces/channelDtos/channelDtos";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import { GetChannelRes } from "../../../interfaces/team/TeamandRegion.modal";
import { GetAllChannelAsync } from "../../../services/teamandregion/TeamandRegionService";
import { useAuth } from "../../../firebase/AuthProvider";
import CreateCustomChannelModal from "../teamPage/modals/CreateCustomChannelModal";
import UserInfo from "../../../interfaces/UserInfo";
import { ROLES } from "../../../Data/SD";

const IoClose = IoCloseOutline as any;
const Search = FiSearch as any;


interface Props {
    addConversationView: boolean;
    users: UserInfo[];
    selectedUsers: string[];
    setSelectedUsers: (ids: string[]) => void;
    handleCreateConversation: () => void;
    onClose: () => void;
    chatClose: () => void;

}

export default function CreateConversationModal({
    addConversationView,
    users,
    selectedUsers,
    setSelectedUsers,
    handleCreateConversation,
    onClose,
    chatClose,
}: Props) {
    const { currentUser, currentRoles } = useAuth();
    const [isChat, setIsChat] = useState(false);

    const CREATE_GROUP_ALLOWED_ROLES = [
        ROLES.OWNER,
        ROLES.PROGRAM_MANAGER,
    ];

    const canCreateGroup = currentRoles?.some(role =>
        CREATE_GROUP_ALLOWED_ROLES.includes(role.name as ROLES)
    );

    // Data
    const [channels, setChannels] = useState<GetChannelRes[]>([]);
    const [selectedChannel, setSelectedChannel] = useState<GetChannelRes | null>(null);
    const [selectedChannelForEdit, setSelectedChannelForEdit] = useState<GetChannelRes>();

    // Others
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showCreateChannelModal, setShowCreateChannelModal] = useState<boolean>(false);
    const [showArchivedChannelModal, setShowArchivedChannelModal] = useState<boolean>(false);

    const [archivedChannelRefreshKey, setArchivedChannelRefreshKey] = useState<number>(0);
    const [showTooltip1, setShowTooltip1] = useState(false);


    useEffect(() => {
        getAllChannel();
    }, [currentUser]);

    const getAllChannel = async () => {
        const token = await currentUser?.getIdToken();
        if (!token) return;

        setLoading(true);
        GetAllChannelAsync(token).then((res) => {
            if (res.success) {
                setChannels(res.data);
            } else {
                toast.error(res.message);
            }
        }).catch((err) => {
            toast.error(err.message);
        }).finally(() => {
            setLoading(false);
        })
    }

    const toggleUser = (id: string) => {
        setSelectedUsers(
            selectedUsers.includes(id)
                ? selectedUsers.filter((uid) => uid !== id)
                : [...selectedUsers, id]
        );
    };

    const handleCloseCreateChannelModal = () => {
        setSelectedChannelForEdit(undefined);
        setShowCreateChannelModal(false);
    }

    const handleShowCreateChannelModal = () => setShowCreateChannelModal(true);

    const handleCloseArchivedChannelModal = () => setShowArchivedChannelModal(false);

    const handleShowArchivedChannelModal = () => setShowArchivedChannelModal(true);

    // const selectedGroupData = useMemo(() => {
    //     if (!selectedGroup) return null;
    //     return groups.find(group => group.id === selectedGroup);
    // }, [selectedGroup, groups]);

    // const groupMembers = useMemo(() => {
    //     if (!selectedGroupData) return [];
    //     return users.filter(user => selectedGroupData.members.includes(user.id));
    // }, [selectedGroupData, users]);

    const getGroupTypeVariant = (type: string) => {
        const variants = {
            COMPANY: 'primary',
            TEAM: 'success',
            CUSTOM: 'info'
        };
        return variants[type as keyof typeof variants] || 'secondary';
    };

    const formatGroupType = (type: string) => {
        return type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');
    };


    const handleEditChannel = (e: React.MouseEvent<HTMLButtonElement>, channel: GetChannelRes) => {
        e.stopPropagation();
        setSelectedChannelForEdit(channel);
        handleShowCreateChannelModal();
    }

    const updateArchivedStatusOfChannel = async (chanelId: string, status: boolean) => {
        const isConfirm = await showConfirmationDialog(
            undefined, // keep default title
            `Do you really want to ${status ? "archive this channel?" : "Remove from archive List?"} ` // override only text
        );

        if (!isConfirm || !currentUser) return;
        const payload: UpdateArchivedStatusOfChannelReq = {
            channelId: chanelId,
            isArchived: status
        }
        const token = await currentUser.getIdToken();
        UpdateArchivedStatusOfChannelAsync(payload, token).then((res) => {
            if (res.success) {
                getAllChannel();
                setArchivedChannelRefreshKey(archivedChannelRefreshKey + 1);
            } else {
                toast.error(res.message);
            }
        }).catch((err) => {
            toast.error(err.message);
        })
    }

    const filteredUsers = users.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const displayName = user.displayName?.toLowerCase() || "";
        const email = user.email?.toLowerCase() || "";

        const term = searchTerm.toLowerCase().trim();

        return (
            fullName.includes(term) ||
            displayName.includes(term) ||
            email.includes(term)
        );
    });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        const aSelected = selectedUsers.includes(a.id);
        const bSelected = selectedUsers.includes(b.id);

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
    });


    return (
        <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black/40">
            <div className="w-[448px] rounded-xl bg-white shadow-xl p-6 mx-[20px]">
                {/* Header */}
                <div className="flex items-center justify-between relative">
                    <h2 className="text-[22px] font-bold">Start New Chat</h2>
                    <button onClick={onClose} className="absolute -top-1.5 -right-1.5">
                        <IoClose className="text-[24px] text-[#0A0A0A]" />{""}
                    </button>
                </div>

                <div className="grid grid-cols-2 justify-center gap-2 mt-[18px]">
                    <button
                        type="button"
                        onClick={() => setIsChat(false)}
                        className={`px-4 py-1.5 text-base font-normal rounded-md transition-all ${isChat
                            ? " text-[#747474] hover:text-gray-700"
                            : "bg-[#F4F4F4] text-[#7E57C2] font-normal"
                            }`}
                    >
                        Create Chat
                    </button>
                    {/* <button
                        type="button"
                        onClick={() => setIsChat(true)}
                        className={`px-4 py-1.5 text-base font-normal rounded-md transition-all ${!isChat
                            ? " text-[#747474] hover:text-gray-700"
                            : "bg-[#F4F4F4] text-[#7E57C2] font-normal"
                            }`}
                    >
                        Create Group
                    </button> */}

                    <div
                        className="relative"
                        onMouseEnter={() => !canCreateGroup && setShowTooltip1(true)}
                        onMouseLeave={() => setShowTooltip1(false)}
                    >
                        <button
                            type="button"
                            disabled={!canCreateGroup}
                            onClick={() => canCreateGroup && setIsChat(true)}
                            className={`w-full px-4 py-1.5 text-base font-normal rounded-md transition-all ${!isChat
                                ? " text-[#747474] hover:text-gray-700"
                                : "bg-[#F4F4F4] text-[#7E57C2] font-normal"
                                } ${!canCreateGroup ? "cursor-not-allowed opacity-60" : ""}`}
                        >
                            Create Group
                        </button>

                        {!canCreateGroup && showTooltip1 && (
                            <div className="absolute left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-xs text-white bg-black rounded whitespace-nowrap z-10">
                                Only Owner or Program Manager <br /> can create a group
                            </div>
                        )}
                    </div>

                </div>

                {
                    isChat ? <>

                        <CreateCustomChannelModal
                            handleCloseCreateChannelModal={handleCloseCreateChannelModal}
                            showCreateChannelModal={showCreateChannelModal}
                            refreshChannelList={getAllChannel}
                            SelectedChannelForEdit={selectedChannelForEdit}
                            onClose={onClose}
                            chatClose={chatClose}

                        />
                    </> : <>
                        <div className="flex items-center bg-[#F9FAFB] rounded-[10px] px-[12px] mt-[18px]" style={{ border: "1px #E5E7EB solid" }}>
                            <Search className=" text-[#99A1AF] " />
                            <input
                                placeholder="Search by name or email"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: "100%",
                                    border: "none",
                                    background: "transparent",
                                    outline: "none",
                                    boxShadow: "none",
                                    color: "#0A0A0A80",
                                    fontWeight: "400",
                                }}
                            />
                        </div>

                        <div className="max-h-[260px] overflow-y-auto my-3 no-scrollbar">
                            {users.length === 0 ? (
                                <p className="text-center text-[#6A7282] py-10 text-sm">
                                    No users found
                                </p>
                            ) : sortedUsers.length === 0 ? (
                                <p className="text-center text-[#6A7282] py-10 text-sm">
                                    No users found
                                </p>
                            ) : (
                                sortedUsers.map((user) => {
                                    const isSelected = selectedUsers.includes(user.id);
                                    const name =
                                        user.displayName || `${user.firstName} ${user.lastName}`;

                                    return (
                                        <div
                                            key={user.id}
                                            onClick={() => toggleUser(user.id)}
                                            className={`mb-1 flex cursor-pointer items-center justify-between rounded-[10px] px-3 py-2 ${isSelected ? "bg-[#F4F4F4]" : "hover:bg-[#F4F4F4]"
                                                }`}
                                        >
                                            <div className="flex gap-x-3 items-center">
                                                <div className="relative w-14 h-14 flex-shrink-0 rounded-full flex justify-center items-center"
                                                    style={{
                                                        border: user.employeeRole === "Participant"
                                                            ? "2px solid #D32F2F"
                                                            : "2px solid #6999FF"
                                                    }}
                                                >
                                                    <AvatarImage
                                                        userId={user.id}
                                                        size="sm"
                                                        displayName={`${user.firstName} ${user.lastName}`}
                                                    />
                                                    {/* Online status dot */}
                                                    {/* <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span> */}
                                                </div>

                                                <div className="">
                                                    <p className="text-[13px] font-medium text-[#101828] leading-tight m-0 capitalize">
                                                        {name}
                                                    </p>
                                                    <p className="text-[11px] text-[#6A7282] !text-start m-0 pt-[2px] uppercase">
                                                        {user.employeeRole}
                                                    </p>
                                                </div>
                                            </div>

                                            <div
                                                className={`flex h-8 w-8 items-center justify-center rounded-md border ${isSelected
                                                    ? "border-[#7E57C2] bg-[#7E57C2] hover:bg-[#6e43b8]"
                                                    : "border-gray-300"
                                                    }`}
                                            >
                                                {isSelected && (
                                                    <svg
                                                        className="h-5 w-5 text-white"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-3 border-t">
                            <button
                                onClick={onClose}
                                className="rounded-[10px] bg-[#F9F6FF] border px-4 py-2 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateConversation}
                                disabled={selectedUsers.length === 0}
                                className="rounded-[10px] bg-[#7E57C2] hover:bg-[#6e43b8] px-4 py-2 text-sm text-white disabled:opacity-50"
                            >
                                Start Chat
                            </button>
                        </div>
                    </>
                }
            </div>
        </div>
    );
}
